import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { powerOutline } from "ionicons/icons";
import { useEffect, useRef } from "react";
import SidebarLink from "./SidebarLink";
import { IonIcon } from "@ionic/react";
import { CustomScroll } from "react-custom-scroll";
// Matches the Tailwind `lg` breakpoint used below (`lg:static`) — the exact
// point where this component switches from a mobile overlay drawer to a
// normal, static, always-visible desktop nav column.
const LARGE_SCREEN_QUERY = '(min-width: 1024px)';
function useIsLargeScreen() {
    const [isLarge, setIsLarge] = useState(()=>typeof window !== 'undefined' && window.matchMedia(LARGE_SCREEN_QUERY).matches);
    useEffect(()=>{
        const mql = window.matchMedia(LARGE_SCREEN_QUERY);
        const onChange = ()=>setIsLarge(mql.matches);
        mql.addEventListener('change', onChange);
        return ()=>mql.removeEventListener('change', onChange);
    }, []);
    return isLarge;
}
export const Sidebar = ({ sidebarOpen, setSidebarOpen, header, theme = 'light', topOptions, bottomOptions, logOutFunc, profile })=>{
    // At `lg`+ this renders as a normal static desktop column (via the
    // `lg:static` classes below), NOT an overlay — so the portal and the
    // brand/navy theme (both meant for the mobile drawer, e.g. to render above
    // the bottom tab bar) only apply below that breakpoint. Otherwise the
    // desktop layout breaks (a portaled, `position:static` element has no
    // sensible place to sit) and desktop gets a treatment it never asked for.
    const isLargeScreen = useIsLargeScreen();
    const dark = theme === 'brand' && !isLargeScreen;
    const sidebar = useRef(null);
    const backdropRef = useRef(null);
    // close if the esc key is pressed
    useEffect(()=>{
        const handler = (e)=>{
            if (!sidebarOpen || e.type == 'keydown' && e.keyCode !== 27) return;
            setSidebarOpen(false);
        };
        if (backdropRef.current) backdropRef.current.addEventListener('click', handler);
        document.addEventListener('keydown', handler);
        return ()=>{
            document.removeEventListener('keydown', handler);
            if (backdropRef.current) backdropRef.current.removeEventListener('click', handler);
        };
    });
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    useEffect(()=>{
        const hideProfileMenu = ()=>{
            setProfileMenuOpen(false);
        };
        document.querySelector('body')?.addEventListener('click', hideProfileMenu);
        return ()=>document.querySelector('body')?.removeEventListener('click', hideProfileMenu);
    }, []);
    const content = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            sidebarOpen && /*#__PURE__*/ _jsx("div", {
                ref: backdropRef,
                className: "lg:hidden z-[990] fixed top-0 left-0 w-full h-full bg-black/10 dark:bg-black/30 backdrop-blur-sm"
            }),
            /*#__PURE__*/ _jsx("aside", {
                ref: sidebar,
                className: `no-select z-[999] fixed top-0 -left-1 h-full flex w-64 flex-col overflow-hidden lg:rounded-md duration-300 ease-linear lg:static lg:translate-x-0 ${dark ? 'bg-[#dcf1ff]' : 'bg-background dark:bg-background-accent border border-border dark:bg-boxdark'} ${sidebarOpen ? 'translate-x-0' : '-translate-x-[150%]'}`,
                children: /*#__PURE__*/ _jsx(CustomScroll, {
                    heightRelativeToParent: "100%",
                    // sidebar-scrollbar-hide: makes the scrollbar thumb/track invisible
                    // for this drawer specifically (see the app-level CSS override) —
                    // the drawer's own content already scrolls fine via touch, it
                    // doesn't need a visible bar.
                    className: "w-[calc(100%+3px)] h-full sidebar-scrollbar-hide",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pr-1",
                        children: [
                            header && /*#__PURE__*/ _jsx("div", {
                                className: "px-4 flex items-center h-7 mt-4",
                                style: {
                                    paddingTop: 'calc(env(safe-area-inset-top) + .7rem)'
                                },
                                children: header
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "pb-48 flex flex-col gap-3 w-full px-2 pt-8",
                                children: topOptions.map((menu, idx)=>/*#__PURE__*/ _jsx(SidebarLink, {
                                        ...menu,
                                        dark: dark,
                                        dismiss: ()=>setSidebarOpen(false)
                                    }, `top-sidebarlink-${idx}`))
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: `fixed bottom-0 w-full ${dark ? 'bg-[#dcf1ff]' : 'bg-background dark:bg-background-accent'}`,
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: "relative min-h-full flex flex-col gap-1 py-2",
                                    children: [
                                        bottomOptions.map((menu, idx)=>/*#__PURE__*/ _jsx(SidebarLink, {
                                                ...menu,
                                                dark: dark,
                                                dismiss: ()=>setSidebarOpen(false),
                                                // Merge, don't overwrite: `{...menu}` above would clobber
                                                // this `mx-2` inset for any item that sets its own
                                                // className (e.g. Log out's red text color) since a
                                                // spread className wins over an earlier one. That left
                                                // Log out flush against the sidebar edges instead of
                                                // aligned with Settings/Help beside it.
                                                className: `mx-2 ${menu.className || ''}`
                                            }, `bottom-sidebarlink-${idx}`)),
                                        /*#__PURE__*/ _jsxs("div", {
                                            onClick: (e)=>{
                                                e.stopPropagation();
                                                setProfileMenuOpen(!profileMenuOpen);
                                            },
                                            className: `relative flex items-center gap-2 p-2 w-[90%] mx-auto rounded-sm group ${dark ? 'border border-[#001865]/20 hover:bg-white/40' : 'border border-border dark:hover:text-primary hover:bg-customs-lavender'}`,
                                            children: [
                                                profileMenuOpen && /*#__PURE__*/ _jsx("div", {
                                                    className: `absolute bottom-[60px] flex flex-col gap-1 w-full p-2 rounded max-h-[50vh] overflow-auto -ml-2 mx-auto rounded-sm ${dark ? 'border border-[#001865]/20 bg-[#dcf1ff]' : 'border border-border bg-background dark:bg-background-accent'}`,
                                                    children: /*#__PURE__*/ _jsxs("a", {
                                                        // gap-3 (not gap-2) — matches the icon-to-text gap
                                                        // every other SidebarLink row uses, so "Log out"
                                                        // lines up with the rest of the nav instead of
                                                        // sitting at a slightly different rhythm.
                                                        className: dark ? 'flex gap-3 items-center justify-start py-2 px-3 hover:opacity-100 cursor-pointer rounded !text-[#001865]/80 font-medium bg-transparent border-l-4 border-transparent hover:!text-[#001865] hover:bg-white/40' : 'flex gap-3 items-center justify-start py-2 px-3 hover:opacity-100 cursor-pointer rounded text-text-dark-medium dark:text-text-light-medium font-medium bg-transparent border-l-4 border-transparent hover:text-primary dark:hover:text-primary hover:border-lavender hover:bg-customs-lavender',
                                                        onClick: (e)=>{
                                                            e.preventDefault();
                                                            setSidebarOpen(false);
                                                            logOutFunc();
                                                        },
                                                        children: [
                                                            /*#__PURE__*/ _jsx(IonIcon, {
                                                                icon: powerOutline,
                                                                className: `text-inherit text-base`
                                                            }),
                                                            /*#__PURE__*/ _jsx("p", {
                                                                className: "text-sm",
                                                                children: "Log out"
                                                            })
                                                        ]
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "w-8 h-8 bg-surface-primary rounded-full overflow-hidden",
                                                    children: profile.image && /*#__PURE__*/ _jsx("img", {
                                                        src: profile.image,
                                                        className: "w-full h-full object-cover"
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "cursor-pointer w-[calc(100%-60px)]",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: `text-sm w-full truncate ${dark ? '!text-[#001865] group-hover:!text-[#001865]' : 'text-text-dark-high dark:text-text-light-high group-hover:text-primary'}`,
                                                            children: profile.name
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: `text-sm w-full truncate ${dark ? '!text-[#001865]/70 group-hover:!text-[#001865]/70' : 'text-text-dark-medium dark:text-text-light-medium group-hover:text-primary'}`,
                                                            children: profile.email
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("svg", {
                                                    width: "8",
                                                    height: "11",
                                                    viewBox: "0 0 8 11",
                                                    fill: "none",
                                                    xmlns: "http://www.w3.org/2000/svg",
                                                    className: dark ? '' : 'group-hover:[&>path]:stroke-text-dark-high',
                                                    children: /*#__PURE__*/ _jsx("path", {
                                                        d: "M1.33398 7.5026L4.00065 10.1693L6.66732 7.5026M6.66732 3.5026L4.00065 0.835938L1.33398 3.5026",
                                                        stroke: dark ? '#00186599' : '#CDD0D5',
                                                        strokeWidth: "1.5",
                                                        strokeLinecap: "round",
                                                        strokeLinejoin: "round"
                                                    })
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: `absolute -top-8 left-0 w-full h-8 bg-gradient-to-t to-transparent pointer-events-none ${dark ? 'from-[#dcf1ff]' : 'dark:from-background-accent from-background'}`
                                        })
                                    ]
                                })
                            })
                        ]
                    })
                })
            })
        ]
    });
    // Portal to <body> — but ONLY when acting as a mobile overlay (< lg). The
    // sidebar is used inside deeply-nested Ionic page trees there, and Ionic's
    // per-page transition wrappers create their own stacking contexts; a high
    // z-index alone can't escape an ancestor's stacking context, which was
    // letting other fixed UI (e.g. a bottom tab bar) render on top of an open
    // drawer. Portaling to body guarantees it stacks above everything else.
    // At `lg`+ the sidebar is `position: static` and must stay in its normal
    // DOM position to lay out as the desktop's persistent nav column — a
    // portaled static element has no sensible place to sit, which broke the
    // desktop layout entirely.
    if (typeof document === 'undefined' || isLargeScreen) return content;
    return /*#__PURE__*/ createPortal(content, document.body);
};
export default Sidebar;

//# sourceMappingURL=Sidebar.js.map